import React, { useEffect, useRef } from "react";

/**
 * Live audio visualizer.
 * Renders a horizontal row of bars driven by the audio element's frequency data.
 * Falls back to a calm idle animation when the audio is not playing or before user gesture.
 *
 * Props:
 *  - audioRef: ref pointing to the <audio> element
 *  - playing: boolean
 *  - variant: "bars" (default) | "wave"
 *  - className: extra classes on the wrapper
 *  - color: bar color
 */
export default function AudioVisualizer({
  audioRef,
  playing,
  variant = "bars",
  className = "",
  color = "#FF3B30",
}) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const dataRef = useRef(null);
  const rafRef = useRef(0);
  const phaseRef = useRef(0);

  // Lazy create AudioContext + AnalyserNode once playback begins (autoplay policy).
  useEffect(() => {
    if (!playing) return;
    const audioEl = audioRef?.current;
    if (!audioEl) return;
    if (ctxRef.current) {
      ctxRef.current.resume?.();
      return;
    }
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const source = ctx.createMediaElementSource(audioEl);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.82;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      ctxRef.current = ctx;
      analyserRef.current = analyser;
      dataRef.current = new Uint8Array(analyser.frequencyBinCount);
    } catch (e) {
      // MediaElementSource can only be created once per element; ignore if already done.
    }
  }, [playing, audioRef]);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const { clientWidth, clientHeight } = canvas;
      canvas.width = Math.max(1, Math.floor(clientWidth * dpr));
      canvas.height = Math.max(1, Math.floor(clientHeight * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      const analyser = analyserRef.current;
      const data = dataRef.current;
      let values;
      if (analyser && data) {
        analyser.getByteFrequencyData(data);
        values = data;
      } else {
        // idle/fallback: gentle sine pattern, very low amplitude when not playing
        const amp = playing ? 60 : 18;
        const len = 64;
        values = new Uint8Array(len);
        phaseRef.current += playing ? 0.06 : 0.018;
        for (let i = 0; i < len; i++) {
          const v =
            (Math.sin(i * 0.35 + phaseRef.current) +
              Math.sin(i * 0.13 + phaseRef.current * 0.7)) /
            2;
          values[i] = Math.max(0, Math.min(255, 110 + v * amp));
        }
      }

      if (variant === "wave") {
        // Smooth waveform line
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = color;
        ctx.beginPath();
        const len = values.length;
        for (let i = 0; i < len; i++) {
          const x = (i / (len - 1)) * w;
          const v = values[i] / 255;
          const y = h - v * h * 0.9 - h * 0.05;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        // soft fill below
        ctx.lineTo(w, h);
        ctx.lineTo(0, h);
        ctx.closePath();
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, "rgba(255,59,48,0.18)");
        grad.addColorStop(1, "rgba(255,59,48,0)");
        ctx.fillStyle = grad;
        ctx.fill();
      } else {
        // bars
        const barCount = 48;
        const step = Math.floor(values.length / barCount) || 1;
        const gap = 2;
        const barW = (w - gap * (barCount - 1)) / barCount;
        for (let i = 0; i < barCount; i++) {
          let sum = 0;
          for (let j = 0; j < step; j++) sum += values[i * step + j] || 0;
          const v = sum / step / 255;
          const bh = Math.max(2, v * h * 0.95);
          const x = i * (barW + gap);
          const y = h - bh;
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.35 + v * 0.65;
          ctx.fillRect(x, y, barW, bh);
        }
        ctx.globalAlpha = 1;
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [variant, color, playing]);

  return (
    <div className={className}>
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
}
